import { FC, useRef, useState } from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Icon } from '@iconify/react';
import { NodeObject } from 'react-force-graph-2d';
import { determineRiskType } from '../../../../elements/RiskIndicator/functions';
import { NodeContextMenu, NodeType } from '../../Context';
import {
  getExpansionIconName,
  GraphExpansion,
} from '../../GraphControls/ExpansionSelector';
import { getImpactIconName } from '../../../../elements/ImpactIndicator/functions';

type GraphNode = NodeObject & {
  expansion?: GraphExpansion;
  risk?: string;
  impact?: string;
  label?: string;
};

const NODE_TYPE_COLORS: Record<string, string> = {
  Publications: '#f59e0b',
  Authors: '#10b981',
  Affiliations: '#6366f1',
  Keywords: '#06b6d4',
  Awards: '#ec4899',
};

const PUB_COLOR = '#f59e0b';

function getExpansionColor(
  expansion: GraphExpansion | undefined,
  riskColor: string,
): string {
  if (expansion && NODE_TYPE_COLORS[expansion]) {
    return NODE_TYPE_COLORS[expansion];
  }
  return riskColor;
}

export interface NodeItemActions {
  onNodeHover: (node: NodeObject) => void;
  onNodeLeave: (node: NodeObject) => void;
  onNodeClick: (node: NodeObject) => void;
  onNodeChosen: (source: string, target: string, type: NodeType) => void;
  onRemoveNodeClick: (node: NodeObject) => void;
}

interface NodeItemProps extends NodeItemActions {
  node: NodeObject;
  nodes: NodeObject[];
}

export const NodeItem: FC<NodeItemProps> = ({
  node,
  nodes,
  onNodeHover,
  onNodeLeave,
  onNodeClick,
  onNodeChosen,
  onRemoveNodeClick,
}) => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const graphNode = node as GraphNode;
  const isPublication = graphNode.expansion === 'Publications';
  const riskColor = determineRiskType([{ level: graphNode.risk }]).color;
  const nodeAccentColor = getExpansionColor(graphNode.expansion, riskColor);

  return (
    <Stack
      direction={'row'}
      spacing={1}
      key={node.id}
      onMouseEnter={() => {
        onNodeHover(node);
      }}
      onMouseLeave={() => {
        onNodeLeave(node);
      }}
      onClick={() => {
        onNodeClick(node);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        setOpenMenu(true);
      }}
      justifyContent="space-between"
      alignItems="center"
      sx={{
        p: 1,
        cursor: 'pointer',
        color: '#c5cae9',
        borderRadius: 1,
        borderColor: `${nodeAccentColor}40`,
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeft: `4px solid ${nodeAccentColor}`,
        backgroundColor: isPublication
          ? 'rgba(40,22,0,0.5)'
          : 'rgba(15,20,40,0.6)',
        '&:hover': {
          color: 'white',
          backgroundColor: `${nodeAccentColor}18`,
          borderColor: `${nodeAccentColor}70`,
          borderLeft: `4px solid ${nodeAccentColor}`,
        },
      }}
    >
      <Stack direction={'row'} alignItems={'center'} spacing={2}>
        <Stack
          sx={{
            color: nodeAccentColor,
            fontSize: 25,
          }}
        >
          <Icon
            icon={
              graphNode.impact
                ? getImpactIconName(graphNode.impact, 'rounded')
                : getExpansionIconName(graphNode.expansion)
            }
            fontSize={25}
          />
        </Stack>
        <Typography
          sx={{
            color: isPublication ? '#fde68a' : undefined,
            fontSize: isPublication ? '0.8rem' : undefined,
            lineHeight: isPublication ? 1.3 : undefined,
            wordBreak: 'break-word',
          }}
        >
          {graphNode.label || node.id}
        </Typography>
      </Stack>
      <Stack
        direction={'row'}
        alignItems="center"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <IconButton
          ref={iconButtonRef}
          sx={{
            width: 35,
            height: 35,
            color: '#6b7280',
            '&:hover': {
              color: '#c5cae9',
              backgroundColor: 'rgba(99,102,241,0.2)',
            },
          }}
          onClick={() => {
            setOpenMenu(!openMenu);
          }}
        >
          <Icon icon="charm:menu-kebab" />
        </IconButton>
        <NodeContextMenu
          title={node.id as string}
          nodes={nodes}
          nodeContext={
            openMenu
              ? {
                  node,
                  xy: [
                    iconButtonRef?.current?.getClientRects()[0].left,
                    iconButtonRef?.current?.getClientRects()[0].top,
                  ],
                }
              : null
          }
          onClose={() => {
            setOpenMenu(false);
          }}
          onNodeChosen={(source, target, type) => {
            setOpenMenu(false);
            onNodeChosen(source, target, type);
          }}
          onRemoveNodeClick={(node) => {
            setOpenMenu(false);
            onRemoveNodeClick(node);
          }}
        />
        {[
          'Affiliations',
          'Authors',
          'Keywords',
          'Publications',
          'Awards',
        ].includes(graphNode.expansion) && (
          <Tooltip title={'Open dossier'} followCursor>
            <IconButton
              size={'small'}
              sx={{
                width: 35,
                height: 35,
                backgroundColor: `${nodeAccentColor}18`,
                color: nodeAccentColor,
                '&:hover': {
                  backgroundColor: `${nodeAccentColor}30`,
                  color: '#ffffff',
                },
              }}
              onClick={() => {
                function getVisualizationPageName() {
                  switch (graphNode.expansion) {
                    case 'Affiliations':
                      return 'affiliation';
                    case 'Authors':
                      return 'person';
                    case 'Keywords':
                      return 'technology';
                    case 'Publications':
                      return 'publication';
                    case 'Awards':
                      return 'award';
                    default:
                      return 'affiliation';
                  }
                }

                window.open(
                  `/${getVisualizationPageName()}/${encodeURIComponent(
                    String(node.id ?? ''),
                  )}`,
                  '_blank',
                  'noopener noreferrer',
                );
              }}
            >
              <Tooltip title={'Open dossier'} followCursor>
                <Icon icon="iconoir:open-new-window" />
              </Tooltip>
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};
